import { Bouton } from '../ui/composants/bouton'
import { useLiveQuery } from 'dexie-react-hooks'
import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useRole } from '../lib/compte'
import { totauxImpayes } from '../lib/impayes'
import { formatEuro } from '../lib/money'
import { getBase } from '../outbox/base'
import { lister } from '../outbox/depot'
import { ENTREES } from '../ui/entrees'
import { useImpayes } from '../ui/impayes'
import { LigneDevis, useDevisAvecClient } from '../ui/liste-devis'
import { RappelDemain } from '../ui/rappel'
import { RappelRetenues } from '../ui/retenue'
import { BarreActions } from '../ui/synchro'

export default function Accueil() {
  const base = getBase()
  const devis = useDevisAvecClient()
  const role = useRole()
  const impayes = useImpayes()
  const t = impayes ? totauxImpayes(impayes) : null
  const nbChantiers = useLiveQuery(async () => (await lister(base, 'chantiers')).filter((c) => !c.termine).length, [], 0)
  const nbClients = useLiveQuery(async () => (await lister(base, 'clients')).length, [], 0)
  const nbArticles = useLiveQuery(async () => (await lister(base, 'articles')).length, [], 0)

  // Titre et sous-titre propres à l'accueil : les compteurs remplacent la description générique
  const precisions: Record<string, { titre?: string; description?: string }> = {
    '/clients': { titre: `Clients (${nbClients})` },
    '/chantiers': { description: `${nbChantiers} en cours` },
    '/bibliotheque': { description: `${nbArticles} article${nbArticles > 1 ? 's' : ''}` },
    '/impayes': { description: t && t.nombre > 0 ? `${t.nombre} · ${formatEuro(t.total)}` : undefined },
  }

  return (
    <>
      <main className="flex flex-1 flex-col gap-4 p-4">
        <RappelDemain />
        <RappelRetenues />

        <ul className="grid grid-cols-2 gap-2 lg:grid-cols-3">
          {ENTREES.filter((e) => !e.proprietaire || role === 'proprietaire').map(({ to, titre, description, Icone, couleur }) => {
            const p = precisions[to]
            const enRetard = to === '/impayes' ? (t?.nombreRetard ?? 0) : 0
            return (
              <li key={to}>
                <Link
                  to={to}
                  className="relative flex h-full min-h-[4.5rem] items-center gap-2.5 rounded-2xl border border-slate-200 bg-white p-2.5 shadow-sm active:bg-slate-50"
                >
                  <span className={`flex size-10 shrink-0 items-center justify-center rounded-xl text-white ${couleur}`}>
                    <Icone aria-hidden className="size-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-bold leading-tight">{p?.titre ?? titre}</span>
                    <span className="block text-xs leading-tight text-slate-700">{p?.description ?? description}</span>
                  </span>
                  {enRetard > 0 && (
                    <span
                      role="img"
                      aria-label={`${enRetard} en retard`}
                      className="absolute -top-2 -right-1 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-red-600 px-1 text-xs font-bold text-white"
                    >
                      {enRetard}
                    </span>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>

        <section aria-labelledby="dernieres-operations" className="rounded-2xl bg-slate-100 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h2 id="dernieres-operations" className="text-lg font-bold">Dernières opérations</h2>
            <Link to="/devis" className="flex items-center gap-1 text-sm font-medium">
              Voir tout
              <ChevronRight aria-hidden className="size-4" />
            </Link>
          </div>
          {devis?.length === 0 && <p className="text-slate-700">Aucun devis pour l'instant : « + Devis » pour commencer.</p>}
          <ul className="flex flex-col gap-2">
            {devis?.slice(0, 3).map((d) => (
              <li key={d.id}>
                <LigneDevis d={d} />
              </li>
            ))}
          </ul>
        </section>
      </main>

      <BarreActions synchro>
        {/* Clic 1 du parcours Express */}
        <Bouton asChild className="flex-1">
          <Link to="/devis/nouveau">
            + Devis
          </Link>
        </Bouton>
      </BarreActions>
    </>
  )
}
